import { forwardRef, type ReactNode } from 'react';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';

import { cn } from '../../lib/cn';
import type { ZIndex } from '../../tokens/z-index';

// ---- Root ----
export interface DropdownMenuProps extends DropdownMenuPrimitive.DropdownMenuProps {
  children: ReactNode;
}

export function DropdownMenu({ children, modal = false, ...rest }: DropdownMenuProps) {
  return (
    // modal={false} allows the outside click that closes the dropdown to
    // also trigger handlers on elements behind it (e.g. clicking "Convidar
    // consultor" while the Colunas dropdown is open should open the modal
    // in a single click, not two). Callers can still pass `modal` to opt in
    // to the focus-trap behaviour case-by-case.
    <DropdownMenuPrimitive.Root modal={modal} {...rest}>
      {children}
    </DropdownMenuPrimitive.Root>
  );
}

// ---- Trigger ----
export interface DropdownMenuTriggerProps extends DropdownMenuPrimitive.DropdownMenuTriggerProps {}

export const DropdownMenuTrigger = forwardRef<HTMLButtonElement, DropdownMenuTriggerProps>(
  function DropdownMenuTrigger({ children, ...rest }, ref) {
    return (
      <DropdownMenuPrimitive.Trigger ref={ref} asChild {...rest}>
        {children}
      </DropdownMenuPrimitive.Trigger>
    );
  },
);

// ---- Content ----
export interface DropdownMenuContentProps extends DropdownMenuPrimitive.DropdownMenuContentProps {
  /**
   * Override do tier de z-index do conteúdo portal-rendered, aplicado via style
   * inline (vence a classe `z-[500]`). Raramente necessário: o default
   * `Z.FLOATING` (500) já abre acima de qualquer modal (Z.MODAL = 400).
   */
  zIndex?: ZIndex;
}

export function DropdownMenuContent({
  children,
  className,
  sideOffset = 4,
  align = 'end',
  zIndex,
  style,
  ...rest
}: DropdownMenuContentProps) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        sideOffset={sideOffset}
        align={align}
        style={zIndex != null ? { ...style, zIndex } : style}
        className={cn(
          // Z.FLOATING (500) — portal-rendered floating UI sits above any modal
          // (400) by default, so a DropdownMenu inside a Dialog/Sheet is never
          // hidden behind the backdrop. No per-call-site config needed.
          'z-[500] min-w-[8rem] overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-md',
          'animate-in fade-in-0 zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...rest}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
}

// ---- Item ----
export interface DropdownMenuItemProps extends DropdownMenuPrimitive.DropdownMenuItemProps {
  destructive?: boolean;
}

export function DropdownMenuItem({
  children,
  className,
  destructive = false,
  ...rest
}: DropdownMenuItemProps) {
  return (
    <DropdownMenuPrimitive.Item
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm font-body outline-none transition-colors',
        destructive
          ? 'text-red-600 focus:bg-red-50 focus:text-red-700'
          : 'text-slate-700 focus:bg-slate-100 focus:text-secondary-950',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...rest}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  );
}

// ---- Checkbox Item ----
export interface DropdownMenuCheckboxItemProps
  extends DropdownMenuPrimitive.DropdownMenuCheckboxItemProps {}

export function DropdownMenuCheckboxItem({
  children,
  className,
  checked,
  ...rest
}: DropdownMenuCheckboxItemProps) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      checked={checked}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm font-body text-slate-700 outline-none transition-colors',
        'focus:bg-slate-100 focus:text-secondary-950',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...rest}
    >
      {/* Fixed-width slot keeps labels vertically aligned whether or not
          the indicator is currently rendered (Radix only mounts the
          ItemIndicator subtree when state=checked). */}
      <span
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-alamo-orange-500"
        aria-hidden="true"
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      <span className="flex-1 truncate">{children}</span>
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

// ---- Label ----
export function DropdownMenuLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <DropdownMenuPrimitive.Label
      className={cn('px-2 py-1.5 text-xs font-semibold text-slate-500', className)}
    >
      {children}
    </DropdownMenuPrimitive.Label>
  );
}

// ---- Separator ----
export function DropdownMenuSeparator({ className }: { className?: string }) {
  return (
    <DropdownMenuPrimitive.Separator className={cn('-mx-1 my-1 h-px bg-slate-200', className)} />
  );
}
